'use client'

/**
 * Finishes an in-app anchor link that crossed pages
 * (`@/lib/navigation/in-app-anchor`): after a navigation to `/path#id`, it
 * waits for the page to render `id` ON SCREEN — pages stream in, and some
 * bring the target on screen through a resolver — then highlights and
 * focuses it once.
 *
 * "In the document" is not enough: a page under `loading.tsx` is parked by
 * React in a hidden `S:` div before it is moved into place, and the first
 * poll can find the target there. Focusing it then fails silently and left
 * focus on `<body>` (#16598), so `revealInAppAnchor` reports a hidden
 * target as not yet handled and this keeps polling.
 *
 * Mounted once, in the root layout. Renders nothing.
 */

import { usePathname } from 'next/navigation'
import { useEffect } from 'react'

import {
  decodeInAppAnchorId,
  revealInAppAnchor,
} from '@/lib/navigation/in-app-anchor'

/** How long to wait for a streamed page to render the target (ms). */
const ARRIVAL_WAIT_MS = 4000
const ARRIVAL_POLL_MS = 150

export function InAppAnchorOnArrival() {
  const pathname = usePathname()

  useEffect(() => {
    let timer: ReturnType<typeof setInterval> | null = null

    const reveal = () => {
      if (timer !== null) clearInterval(timer)
      timer = null
      const anchorId = decodeInAppAnchorId(window.location.hash.slice(1))
      if (anchorId === '') return
      if (revealInAppAnchor(anchorId)) return
      const startedAt = Date.now()
      timer = setInterval(() => {
        if (
          revealInAppAnchor(anchorId) ||
          Date.now() - startedAt > ARRIVAL_WAIT_MS
        ) {
          if (timer !== null) clearInterval(timer)
          timer = null
        }
      }, ARRIVAL_POLL_MS)
    }

    reveal()
    window.addEventListener('hashchange', reveal)
    return () => {
      window.removeEventListener('hashchange', reveal)
      if (timer !== null) clearInterval(timer)
    }
  }, [pathname])

  return null
}
